import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SidebarProvider } from "@/components/ui/sidebar";
import PrivateRoute from "./components/PrivateRoute";
import Index from "./pages/Index";
import ReportPage from "./pages/ReportPage";
import Dashboard from "./pages/Dashboard";
import DashboardAluguel from "./pages/DashboardAluguel";
import AnalisarImovel from "./pages/AnalisarImovel";
import NotFound from "./pages/NotFound";
import TermosDeUso from "./pages/TermosDeUso";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import ConfirmSignup from "./pages/ConfirmSignup";
import Admin from "./pages/Admin";
import Home from "./pages/Home";
import AgencyAnalytics from "./pages/AgencyAnalytics";
import { useEffect } from "react";
import DashboardSairamUltimaSemana from "./pages/DashboardSairamUltimaSemana";
import DashboardEntraramUltimaSemana from "./pages/DashboardEntraramUltimaSemana";
import BrokerNovosUsuarios from "./pages/BrokerNovosUsuarios";

// Import the styles for sidebar
import "@/components/ui/sidebar.css";

// Configure the query client with error logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
      meta: {
        onError: (error: Error) => {
          console.error('React Query error:', error);
        }
      }
    },
  },
});

// Component to track page views
const PageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Send pageview to Google Analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', 'G-YZLP0SDEFD', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);
  
  return null;
};

// Component to conditionally render header and footer
const HeaderFooterWrapper = () => {
  const location = useLocation();
  const hideHeaderFooterPaths = [
    '/login', 
    '/signup', 
    '/reset-password', 
    '/reset-password-confirm', 
    '/confirm-signup',
    '/home',
    '/dashboard-venda',
    '/dashboard-aluguel',
    '/dashboard-sairam-ultima-semana',
    '/dashboard-entraram-ultima-semana',
    '/agency-analytics',
    '/admin',
    '/broker-novos-usuarios' // Add the new route to hide header/footer
  ];
  
  const shouldHideHeader = hideHeaderFooterPaths.includes(location.pathname);
  const shouldHideFooter = hideHeaderFooterPaths.includes(location.pathname);
  
  // Apply SidebarProvider to specific routes that use the sidebar
  const needsSidebarProvider = [
    '/home', 
    '/dashboard-venda',
    '/dashboard-aluguel',
    '/dashboard-sairam-ultima-semana',
    '/dashboard-entraram-ultima-semana',
    '/agency-analytics',
    '/admin'
  ].includes(location.pathname);
  
  const renderRoutes = () => (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/home" element={
        <PrivateRoute>
          <Home />
        </PrivateRoute>
      } />
      <Route path="/analisar-imovel" element={<AnalisarImovel />} />
      <Route path="/report" element={<ReportPage />} />
      <Route path="/dashboard-venda" element={
        <PrivateRoute>
          <Dashboard />
        </PrivateRoute>
      } />
      <Route path="/dashboard-aluguel" element={
        <PrivateRoute>
          <DashboardAluguel />
        </PrivateRoute>
      } />
      <Route path="/dashboard-sairam-ultima-semana" element={
        <PrivateRoute>
          <DashboardSairamUltimaSemana />
        </PrivateRoute>
      } />
      <Route path="/dashboard-entraram-ultima-semana" element={
        <PrivateRoute>
          <DashboardEntraramUltimaSemana />
        </PrivateRoute>
      } />
      <Route path="/dashboard" element={<Navigate to="/dashboard-venda" replace />} />
      <Route path="/agency-analytics" element={
        <PrivateRoute>
          <AgencyAnalytics />
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute>
          <Admin />
        </PrivateRoute>
      } />
      <Route path="/termos-de-uso" element={<TermosDeUso />} />
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
      <Route path="/confirm-signup" element={<ConfirmSignup />} />
      <Route path="/broker-novos-usuarios" element={<BrokerNovosUsuarios />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
  
  return (
    <>
      <PageTracker />
      {!shouldHideHeader && <Header />}
      <div className={`flex-grow ${!shouldHideHeader ? 'pt-16' : ''}`}>
        {needsSidebarProvider ? (
          <SidebarProvider>
            <div className="min-h-screen flex w-full">
              {renderRoutes()}
            </div>
          </SidebarProvider>
        ) : (
          renderRoutes()
        )}
      </div>
      {!shouldHideFooter && <Footer />}
    </>
  );
};

const App = () => {
  console.log('App component rendering');
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <div className="min-h-screen flex flex-col">
              <HeaderFooterWrapper />
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
